import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';

/**
 * Shimmer animation for the Nike AI button
 * Creates a moving gradient effect
 */
const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

/**
 * Shimmer button for Nike AI
 * Features:
 * - Circular shape
 * - Shimmer background animation
 * - Lime green border (#bad168)
 * - Hover state with background color
 * - Two sizes: small (48px) and large (62px desktop / 56px mobile)
 */
export const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid #bad168;
  border-radius: 50%;
  background: #ffffff;
  background-image: linear-gradient(
    90deg,
    transparent 0%,
    rgba(186, 209, 104, 0.1) 25%,
    rgba(186, 209, 104, 0.2) 50%,
    rgba(186, 209, 104, 0.1) 75%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 3s ease-in-out infinite;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;

  /* Size variants */
  ${({ size }) =>
    size === 'small'
      ? `
    width: 48px;
    height: 48px;
    padding: 12px;
    
    /* Icon sizing for small */
    > div {
      width: 24px;
      height: 24px;
    }
  `
      : `
    width: 62px;
    height: 62px;
    padding: 13px;
    
    /* Icon sizing for large */
    > div {
      width: 36px;
      height: 36px;
    }
    
    /* Mobile: slightly smaller large button */
    @media (max-width: 960px) {
      width: 56px;
      height: 56px;
      padding: 12px;
      
      > div {
        width: 32px;
        height: 32px;
      }
    }
  `}

  &:hover {
    background-color: rgba(186, 209, 104, 0.05);
    animation: none;
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid var(--podium-cds-color-black);
    outline-offset: 2px;
  }

  &:active {
    background-color: rgba(186, 209, 104, 0.1);
    transform: scale(0.98);
  }
`;
